import type { Network, Block, Transaction } from './types'

// GCP prodexer API endpoints.
//
// When the SPA is served from the Go backend (production), API calls go
// through the backend's reverse proxy at /api/proxy/ — the backend resolves
// the Tailscale FQDNs server-side and forwards to prodexer.
//
// When running in dev mode (bun run dev on :5173), the browser calls the
// prodexer Tailscale FQDNs directly (CORS is unrestricted on the prodexer API).
const DEV_API_HOSTS: Record<Network, string> = {
  testnet: 'https://gcp-prodexer-testnet.tail110056.ts.net',
  mainnet: 'https://gcp-prodexer-mainnet.tail110056.ts.net',
}

// Detect if we're served from the Go backend (production) or dev server.
// The Go backend serves on :8080 and has /api/proxy/; dev is :5173.
const IS_PROD = typeof window !== 'undefined' && window.location.port !== '5173'

const PROXY_BASE = '/api/proxy'

function getApiHost(network: Network): string {
  if (IS_PROD) {
    // Production: route through the Go backend proxy.
    // The network is passed as a query param to the proxy.
    return PROXY_BASE
  }
  return DEV_API_HOSTS[network]
}

const API_PREFIX = '/api/v1'

// --- PostgREST-style filter operators (matching handlers.go) ---
type FilterOp =
  | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'like' | 'ilike' | 'in' | 'all' | 'is' | 'between'

interface Filter {
  column: string
  op: FilterOp
  value: string
}

interface QueryParams {
  select?: string[]
  filters?: Filter[]
  order?: { column: string; desc?: boolean }[]
  limit?: number
  offset?: number
  distinct?: boolean
}

class ProdexerClient {
  private network: Network

  constructor(network: Network) {
    this.network = network
  }

  get baseUrl(): string {
    return `${getApiHost(this.network)}${IS_PROD ? '' : API_PREFIX}`
  }

  get networkLabel(): string {
    return this.network
  }

  setNetwork(network: Network) {
    this.network = network
  }

  /**
   * Build a PostgREST-style query string from structured params.
   * Matches the prodexer API's parsing in handlers.go parseQuerySpec().
   * In production (Go backend proxy), adds network= param.
   */
  private buildQuery(params: QueryParams): string {
    const parts: string[] = []

    // In production, the proxy needs to know which network to forward to
    if (IS_PROD) {
      parts.push(`network=${this.network}`)
    }

    if (params.select?.length) {
      parts.push(`select=${encodeURIComponent(params.select.join(','))}`)
    }

    if (params.distinct) {
      parts.push('distinct')
    }

    if (params.order?.length) {
      const orderStr = params.order
        .map((o) => `${o.column}.${o.desc ? 'desc' : 'asc'}`)
        .join(',')
      parts.push(`order=${encodeURIComponent(orderStr)}`)
    }

    if (params.limit !== undefined) {
      parts.push(`limit=${params.limit}`)
    }

    if (params.offset !== undefined) {
      parts.push(`offset=${params.offset}`)
    }

    for (const f of params.filters ?? []) {
      const val = `${f.op}.${f.value}`
      parts.push(`${encodeURIComponent(f.column)}=${encodeURIComponent(val)}`)
    }

    return parts.length > 0 ? `?${parts.join('&')}` : ''
  }

  /**
   * Query a table with structured params. Returns typed array.
   * The API enforces index usage — queries without a usable index return 400.
   */
  async query<T>(table: string, params: QueryParams = {}): Promise<T[]> {
    const url = `${this.baseUrl}/${table}${this.buildQuery(params)}`
    const resp = await fetch(url, {
      headers: { Accept: 'application/json' },
    })

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ error: resp.statusText }))
      const err = new Error(
        `API ${resp.status}: ${body.error || resp.statusText}`
      ) as Error & { status: number; indexes?: unknown[] }
      err.status = resp.status
      err.indexes = body.indexes
      throw err
    }

    const json = await resp.json()
    // Prodexer returns null for empty result sets — normalize to []
    return Array.isArray(json) ? json : []
  }

  /**
   * Query a custom API endpoint (active_inferers, topic_activity, etc.)
   * These take plain query params, not PostgREST-style filters.
   */
  async customQuery<T>(
    name: string,
    params: Record<string, string | number>
  ): Promise<T[]> {
    const search = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      search.set(k, String(v))
    }
    const url = `${this.baseUrl}/${name}?${search.toString()}`
    const resp = await fetch(url, {
      headers: { Accept: 'application/json' },
    })

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ error: resp.statusText }))
      throw new Error(
        `API ${resp.status}: ${body.error || resp.statusText}`
      )
    }

    const json = await resp.json()
    // Prodexer returns null for empty result sets — normalize to []
    return Array.isArray(json) ? json : []
  }

  /** Fetch the latest block height (for time→height conversion) */
  async fetchLatestHeight(): Promise<number | null> {
    try {
      const blocks = await this.query<Block>('blocks', {
        order: [{ column: 'height', desc: true }],
        limit: 1,
      })
      return blocks[0]?.height ?? null
    } catch {
      return null
    }
  }

  /** Health check — in production, hit the Go backend's /health endpoint
   *  (always 200, confirms the server is running). In dev, hit the prodexer's
   *  /health/ready directly. We use /health (not /ready) because /ready pings
   *  prodexer and returns 503 if the Tailscale egress is still connecting. */
  async health(): Promise<boolean> {
    try {
      const url = IS_PROD
        ? '/health'
        : `${getApiHost(this.network)}/health/ready`
      const resp = await fetch(url)
      return resp.ok
    } catch {
      return false
    }
  }

  // --- Convenience builders for common filter patterns ---

  /** eq filter — the most common pattern */
  static eq(column: string, value: string | number): Filter {
    return { column, op: 'eq', value: String(value) }
  }

  /** gte filter */
  static gte(column: string, value: string | number): Filter {
    return { column, op: 'gte', value: String(value) }
  }

  /** gt filter */
  static gt(column: string, value: string | number): Filter {
    return { column, op: 'gt', value: String(value) }
  }

  /** lt filter */
  static lt(column: string, value: string | number): Filter {
    return { column, op: 'lt', value: String(value) }
  }

  /** lte filter */
  static lte(column: string, value: string | number): Filter {
    return { column, op: 'lte', value: String(value) }
  }

  /** between filter — value is "start.end" */
  static between(column: string, start: string | number, end: string | number): Filter {
    return { column, op: 'between', value: `${start}.${end}` }
  }

  /** in filter — value is "(val1,val2,...)" */
  static in(column: string, values: (string | number)[]): Filter {
    return { column, op: 'in', value: `(${values.join(',')})` }
  }

  /** ilike filter — value is the pattern (auto-wrapped in % if no wildcard) */
  static ilike(column: string, pattern: string): Filter {
    return { column, op: 'ilike', value: pattern }
  }
}

/**
 * Convert a tx_hash to PostgreSQL hex format (\x prefix) for eq filters.
 *
 * Accepts three input formats:
 * 1. Base64 (from API bytea response) — decoded to bytes → hex
 * 2. Raw hex (from txHashToDisplayHex or user paste) — detected before base64
 * 3. \x-prefixed hex (already in PostgreSQL format) — passthrough
 *
 * IMPORTANT: hex check must come BEFORE base64 decode, because hex chars
 * (0-9, A-F, a-f) are also valid base64 chars — atob() would silently
 * decode them to garbage bytes.
 */
function txHashToHex(input: string): string {
  // Already in PostgreSQL format
  if (input.startsWith('\\x')) return input

  // Strip 0x prefix if present
  const cleaned = input.replace(/^0[xX]/, '')

  // Raw hex (from txHashToDisplayHex or user paste) — check BEFORE base64
  // Must be even length and all hex chars
  if (cleaned.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(cleaned)) {
    return '\\x' + cleaned.toLowerCase()
  }

  // Base64 (from API bytea response) — decode to bytes → hex
  try {
    const binary = atob(cleaned)
    let hex = ''
    for (let i = 0; i < binary.length; i++) {
      hex += binary.charCodeAt(i).toString(16).padStart(2, '0')
    }
    return '\\x' + hex
  } catch {
    // Unknown format — return as-is and let the API reject it
    return input
  }
}

/**
 * Shared helper: fetch a single transaction by its tx_hash.
 * Converts base64 (from bytea API response) to PostgreSQL hex format.
 * Used by TransactionLookup. TransferExpansion no longer fetches —
 * it shows transfer data directly from the transfers table row.
 */
export async function fetchTransactionByHash(hash: string): Promise<Transaction | null> {
  const rows = await prodexer.query<Transaction>('transactions', {
    filters: [{ column: 'tx_hash', op: 'eq', value: txHashToHex(hash) }],
    limit: 1,
  })
  return rows[0] ?? null
}

// Singleton instance — network is managed by React state
export const prodexer = new ProdexerClient('mainnet')
export { ProdexerClient, txHashToHex }
export type { QueryParams, Filter, FilterOp }
